/**
 * WhatsAppSendOptions — reusable composer block that exposes the
 * "Also send WhatsApp" toggle + template picker. Used by Drawdown Emails,
 * Reminders, and Acknowledgments tabs. Backed by the `useWhatsAppSend` hook.
 *
 * Props
 *   wa:    return value from useWhatsAppSend(drawdownId)
 *   label: copy describing what the parallel WhatsApp send does for this tab
 */
import React from 'react';
import { MessageCircle, AlertCircle, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';

const WhatsAppSendOptions = ({ wa, label = 'fires a parallel WhatsApp send over the same selected recipients.' }) => {
  const { preflight, enabled, setEnabled, templateId, setTemplateId,
          pickedNotReadyReason, canSend } = wa;
  const checking = preflight.reason === 'checking';
  const noTemplates = preflight.eligible
    ? false
    : preflight.reason === 'no_published_drawdown_template';

  // We render the picker as long as there is at least one published template,
  // even if every one of them has unmapped variables — so the admin can SEE
  // their templates and act on the warning. This matches how the Approved
  // Email Template selector works (it doesn't hide itself behind preflight).
  const hasAnyPublished = (preflight.all_published_templates || []).length > 0;

  // The toggle is enable-able only when the picked template is actually
  // send-ready. Lighting it up otherwise would let the admin click "Send"
  // and queue a job that the worker refuses.
  const canToggle = preflight.eligible && hasAnyPublished && !pickedNotReadyReason;

  return (
    <div className="space-y-2" data-testid="dd-send-whatsapp-panel">
      <div className="flex flex-wrap items-start gap-3">
        <label
          className={`flex items-start cursor-pointer ${!canToggle ? 'opacity-60 cursor-not-allowed' : ''}`}
          title={canToggle ? 'Send a WhatsApp message in addition to the email.' : (preflight.message || pickedNotReadyReason || 'Not eligible')}
        >
          <input
            type="checkbox"
            checked={enabled && canToggle}
            onChange={(e) => setEnabled(e.target.checked)}
            disabled={!canToggle}
            className="h-4 w-4 text-blue-600 rounded mt-0.5 focus:ring-blue-500"
            data-testid="dd-send-whatsapp-toggle"
          />
          <span className="ml-2 text-sm text-gray-700 flex items-center flex-wrap">
            <MessageCircle size={12} className={`inline mr-1 ${canToggle ? 'text-green-600' : 'text-gray-400'}`} />
            Also send WhatsApp
            <span className="text-gray-500 ml-1"> — {label}</span>
          </span>
        </label>

        {/* High-level config block — never blocks the picker from showing */}
        {!preflight.eligible && !checking && !hasAnyPublished && (
          <span
            className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200 inline-flex items-center gap-1"
            data-testid="dd-send-whatsapp-disabled-reason"
            data-reason={preflight.reason}
            title={preflight.message}
          >
            <AlertCircle size={11} className="text-amber-600" />
            {preflight.message}
            {noTemplates && (
              <Link to="/whatsapp-templates" className="ml-1 underline" data-testid="dd-send-whatsapp-fix-link">Add a template</Link>
            )}
            {preflight.reason === 'no_active_config' && (
              <Link to="/whatsapp-config" className="ml-1 underline" data-testid="dd-send-whatsapp-fix-link">Configure</Link>
            )}
            {preflight.reason === 'config_not_tested' && (
              <Link to="/whatsapp-config" className="ml-1 underline" data-testid="dd-send-whatsapp-fix-link">Run test</Link>
            )}
          </span>
        )}
      </div>

      {/* Always-visible template picker when ANY published drawdown templates exist.
          Mirrors the "Approved Template *" pattern used by the Email composer.
          Every published template is selectable — unmapped ones surface a per-
          template warning below the picker with a direct link to fix the
          mappings, instead of being silently disabled (which made the picker
          look broken when nothing was send-ready). */}
      {hasAnyPublished && (
        <div className="grid grid-cols-1 md:grid-cols-[260px,1fr] gap-2 items-start" data-testid="dd-send-whatsapp-template-picker">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              WhatsApp Template
              {!preflight.eligible && (
                <span className="text-amber-600 ml-1" title={preflight.message}>(setup needed)</span>
              )}
            </label>
            <select
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:ring-2 focus:ring-blue-500"
              data-testid="dd-send-whatsapp-template-select"
            >
              <option value="">— Pick a WhatsApp template —</option>
              {(preflight.all_published_templates || []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.language_code})
                  {t.header_type === 'document' ? ' · attaches PDF' : ''}
                  {!t.is_send_ready ? ` · ${t.unmapped_count} unmapped` : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="text-xs text-gray-500 mt-5 md:mt-6 flex items-center gap-1 flex-wrap">
            <Lock size={10} className="text-gray-400" />
            <span>
              Honours per-contact <span className="font-medium">WhatsApp Opt-out</span> on the account.
            </span>
            {pickedNotReadyReason && wa.pickedTemplate && (
              <span
                className="w-full mt-1 flex items-start gap-1.5 text-xs text-amber-800 bg-amber-50 px-2 py-1.5 rounded border border-amber-200"
                data-testid="dd-send-whatsapp-picked-not-ready"
                data-reason="picked_template_unmapped"
              >
                <AlertCircle size={12} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <span className="flex-1">
                  <span className="font-medium">&ldquo;{wa.pickedTemplate.name}&rdquo;</span> has{' '}
                  <span className="font-semibold">{wa.pickedTemplate.unmapped_count} unmapped variable{wa.pickedTemplate.unmapped_count === 1 ? '' : 's'}</span>.
                  Map every variable to a Prime AIF placeholder so the WhatsApp message can be populated with live data.
                  {' '}
                  <Link
                    to={`/whatsapp-templates?template=${wa.pickedTemplate.id}`}
                    className="underline font-medium text-amber-900 hover:text-amber-950"
                    data-testid="dd-send-whatsapp-fix-template-link"
                  >
                    Fix this template &rarr;
                  </Link>
                </span>
              </span>
            )}
            {preflight.eligible && templateId && !pickedNotReadyReason && enabled && (
              <span className="text-green-700 ml-1" data-testid="dd-send-whatsapp-ready">
                · Ready to send
              </span>
            )}
            {!preflight.eligible && !checking && hasAnyPublished && !pickedNotReadyReason && (
              <span
                className="text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200 inline-flex items-center gap-1 ml-1"
                data-testid="dd-send-whatsapp-disabled-reason"
                data-reason={preflight.reason}
                title={preflight.message}
              >
                <AlertCircle size={10} className="text-amber-600" />
                {preflight.message}
                {preflight.reason === 'no_active_config' && (
                  <Link to="/whatsapp-config" className="ml-1 underline" data-testid="dd-send-whatsapp-fix-link">Configure</Link>
                )}
                {preflight.reason === 'config_not_tested' && (
                  <Link to="/whatsapp-config" className="ml-1 underline" data-testid="dd-send-whatsapp-fix-link">Run test</Link>
                )}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppSendOptions;
export { WhatsAppSendOptions };
